import { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Container, Input, Label } from "reactstrap";
import { createReservation, fetchBranches, getFloors, getTables } from "../../utils/apiCalls";
import { C, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard } from "../reporting/ReportingShared";
import Breadcrumb from "../common/breadcrumb";

const fieldStyle = { borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14 };
const labelStyle = { fontSize: 12, fontWeight: 600, color: C.textMed, marginBottom: 4 };

const AddReservation = () => {
  const navigate = useNavigate();
  const userType = localStorage.getItem("userType");
  const merchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = userType === "merchant" ? merchantId : reduxSelectedMerchant;

  const [branches, setBranches] = useState([]);
  const [floors, setFloors] = useState([]);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    customerName: "", customerPhone: "", customerEmail: "",
    guestCount: 1, reservationDate: "", startTime: "", endTime: "",
    branchId: "", source: "phone", tableId: "", preferredFloorId: "",
    specialOccasion: "", notes: "",
  });

  useEffect(() => {
    const load = async () => {
      const response = await fetchBranches({}, false);
      let list = response?.data || [];
      if (selectedMerchant && selectedMerchant !== "all") list = list.filter((b) => String(b.MerchantId) === String(selectedMerchant));
      setBranches(list);
    };
    load();
  }, [selectedMerchant]);

  useEffect(() => {
    if (form.branchId) {
      getFloors({ branchId: form.branchId }).then((data) => setFloors(data || []));
      getTables({ branchId: form.branchId }).then((data) => setTables(data || []));
    }
  }, [form.branchId]);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = { ...form };
    if (!payload.tableId) delete payload.tableId;
    else payload.tableId = Number(payload.tableId);
    if (!payload.preferredFloorId) delete payload.preferredFloorId;
    else payload.preferredFloorId = Number(payload.preferredFloorId);
    payload.branchId = Number(payload.branchId);
    payload.guestCount = Number(payload.guestCount);
    const result = await createReservation(payload);
    setLoading(false);
    if (result) navigate("/reservations/list");
  };

  return (
    <Fragment>
      <style>{reportingGlobalCSS}</style>
      <Breadcrumb title="Add Reservation" parent="Reservations" />
      <Container fluid className="rpt-wrap">
        <RCard>
          <form onSubmit={handleSubmit}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div><Label style={labelStyle}>Customer Name *</Label><Input value={form.customerName} onChange={(e) => handleChange("customerName", e.target.value)} required style={fieldStyle} /></div>
              <div><Label style={labelStyle}>Phone</Label><Input value={form.customerPhone} onChange={(e) => handleChange("customerPhone", e.target.value)} placeholder="+966..." style={fieldStyle} /></div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginTop: 16 }}>
              <div><Label style={labelStyle}>Guest Count *</Label><Input type="number" min="1" value={form.guestCount} onChange={(e) => handleChange("guestCount", e.target.value)} required style={fieldStyle} /></div>
              <div><Label style={labelStyle}>Date *</Label><Input type="date" value={form.reservationDate} onChange={(e) => handleChange("reservationDate", e.target.value)} required style={fieldStyle} /></div>
              <div><Label style={labelStyle}>Start Time *</Label><Input type="time" value={form.startTime} onChange={(e) => handleChange("startTime", e.target.value)} required style={fieldStyle} /></div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginTop: 16 }}>
              <div><Label style={labelStyle}>End Time</Label><Input type="time" value={form.endTime} onChange={(e) => handleChange("endTime", e.target.value)} style={fieldStyle} /></div>
              <div><Label style={labelStyle}>Branch *</Label>
                <Input type="select" value={form.branchId} onChange={(e) => handleChange("branchId", e.target.value)} required style={fieldStyle}>
                  <option value="">Select Branch</option>
                  {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </Input>
              </div>
              <div><Label style={labelStyle}>Source</Label>
                <Input type="select" value={form.source} onChange={(e) => handleChange("source", e.target.value)} style={fieldStyle}>
                  {["phone", "walk_in", "online", "pos"].map((s) => <option key={s} value={s}>{s}</option>)}
                </Input>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginTop: 16 }}>
              <div><Label style={labelStyle}>Preferred Floor</Label>
                <Input type="select" value={form.preferredFloorId} onChange={(e) => handleChange("preferredFloorId", e.target.value)} style={fieldStyle}>
                  <option value="">Any</option>
                  {floors.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </Input>
              </div>
              <div><Label style={labelStyle}>Assign Table</Label>
                <Input type="select" value={form.tableId} onChange={(e) => handleChange("tableId", e.target.value)} style={fieldStyle}>
                  <option value="">Auto-assign</option>
                  {tables.filter((t) => t.status === "available").map((t) => <option key={t.id} value={t.id}>{t.name} (#{t.tableNumber})</option>)}
                </Input>
              </div>
              <div><Label style={labelStyle}>Special Occasion</Label><Input value={form.specialOccasion} onChange={(e) => handleChange("specialOccasion", e.target.value)} placeholder="e.g. Birthday" style={fieldStyle} /></div>
            </div>
            <div style={{ marginTop: 16 }}>
              <Label style={labelStyle}>Notes</Label>
              <Input type="textarea" value={form.notes} onChange={(e) => handleChange("notes", e.target.value)} placeholder="Any special requests..." style={{ ...fieldStyle, minHeight: 80 }} />
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24 }}>
              <Button style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed, fontWeight: 600 }} onClick={() => navigate("/reservations/list")}>Cancel</Button>
              <Button type="submit" style={{ background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", borderRadius: 8, fontWeight: 600 }} disabled={loading}>
                {loading ? "Creating..." : "Create Reservation"}
              </Button>
            </div>
          </form>
        </RCard>
      </Container>
    </Fragment>
  );
};

export default AddReservation;
